'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { usePathname } from 'next/navigation';

interface FullscreenContextType {
  isFullscreen: boolean;
  setIsFullscreen: (value: boolean) => void;
  toggleFullscreen: () => void;
}

const FullscreenContext = createContext<FullscreenContextType | undefined>(
  undefined,
);

export function FullscreenProvider({ children }: { children: ReactNode }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pathname = usePathname();

  // Disable fullscreen when route changes away from timing page
  useEffect(() => {
    if (!pathname.startsWith('/app/timing')) {
      setIsFullscreen(false);
      // Exit browser fullscreen if active
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {
          // Ignore errors
        });
      }
    }
  }, [pathname]);

  const toggleFullscreen = useCallback(async () => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);

    // Toggle browser fullscreen
    if (newFullscreenState) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (err) {
        // The request can be denied (iframe without the fullscreen permission,
        // iOS Safari, blocked gesture). Roll the state back — otherwise the
        // page stays in the pseudo-fullscreen layout, which hides the very
        // button that would restore it.
        setIsFullscreen(false);
        console.error('Error attempting to enable fullscreen:', err);
      }
    } else {
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (err) {
          console.error('Error attempting to exit fullscreen:', err);
        }
      }
    }
  }, [isFullscreen]);

  // Handle browser fullscreen changes (e.g., user presses F11 or Esc)
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  return (
    <FullscreenContext.Provider
      value={{ isFullscreen, setIsFullscreen, toggleFullscreen }}
    >
      {children}
    </FullscreenContext.Provider>
  );
}

export function useFullscreen() {
  const context = useContext(FullscreenContext);
  if (context === undefined) {
    throw new Error('useFullscreen must be used within a FullscreenProvider');
  }
  return context;
}
